import { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Helmet } from 'react-helmet';
import { Empty } from 'antd';

import { fetchProductsByBrand } from '../../Redux/slice/productSlice';
import { fetchBrands } from '../../Redux/slice/brandSlice';
import RelatedBrands from './RelatedBrands';

const Brand = () => {
  const { brandId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { brandProducts, loading } = useSelector((state) => state.products);
  const { brands } = useSelector((state) => state.brands);

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12;
  const [imageLoadStates, setImageLoadStates] = useState({});
  const observer = useRef();

  useEffect(() => {
    window.scrollTo(0, 0);
    setCurrentPage(1);
    dispatch(fetchBrands());
    dispatch(fetchProductsByBrand(brandId));
  }, [dispatch, brandId]);

  const selectedBrand = brands.find((brand) => brand.brandId === brandId);
  const filteredBrands = selectedBrand
    ? brands.filter((b) => b.categoryId === selectedBrand.categoryId)
    : [];

  const filteredProducts = (brandProducts || [])
    .filter(
      (product) =>
        product.price >= 0 &&
        product.price <= 200000 &&
        product.productID !== '9d88a301-e4ff-42a2-957a-9c611d4cce12'
    )
    .sort((a, b) => new Date(b.dateCreated) - new Date(a.dateCreated));

  const currentProducts = filteredProducts.slice(0, currentPage * itemsPerPage);

  // Function to calculate pricing details
  const calculatePricing = (product) => {
    const currentPrice = product.price;
    // Simulate old price (30-50% higher than current price)
    const discountPercentage = Math.floor(Math.random() * 21) + 30; // Random between 30-50%
    const oldPrice = Math.round(currentPrice / (1 - discountPercentage / 100));
    
    return {
      currentPrice,
      oldPrice,
      discountPercentage: Math.round(((oldPrice - currentPrice) / oldPrice) * 100)
    };
  };

  const handleImageLoad = (id) =>
    setImageLoadStates((prev) => ({ ...prev, [id]: true }));

  const handleImageError = (id) =>
    setImageLoadStates((prev) => ({ ...prev, [id]: 'error' }));

  const lastProductRef = useCallback(
    (node) => {
      if (loading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          setCurrentPage((prev) => prev + 1);
        }
      });
      if (node) observer.current.observe(node);
    },
    [loading]
  );

  const brandName = selectedBrand?.brandName || 'Featured Brands';
  const pageTitle = selectedBrand
    ? `Shop ${brandName} Products Online | Franko Trading`
    : 'Explore Branded Products | Franko Trading';
  const description = selectedBrand
    ? `Buy genuine ${brandName} electronics and accessories at Franko Trading. Fast delivery and best prices guaranteed.`
    : 'Browse a wide selection of authentic electronics and accessories from top brands.';
  const imageUrl = `${window.location.origin}/assets/frankoIcon.png`;
  const pageUrl = `https://www.frankotrading.com/brand/${brandId}`;

const productListSchema = {
  "@context": "https://schema.org",
  "@type": "Brand",
  "name": brandName,
  "url": pageUrl,
  "logo": imageUrl,
  "description": description,
  "hasOfferCatalog": {
    "@type": "OfferCatalog",
    "name": `${brandName} Product Listings`,
    "itemListElement": currentProducts.map((product) => ({
      "@type": "Product",
      "@id": `${pageUrl}/product/${product.productID}`,
      "name": product.productName,
      "sku": product.productID,
      "image": `https://smfteapi.salesmate.app/Media/Products_Images/${product.productImage.split('\\').pop()}`,
      "description": product.description,
      "brand": {
        "@type": "Brand",
        "name": brandName
      },
      "offers": {
        "@type": "Offer",
        "priceCurrency": "GHS",
        "price": product.price,
        "priceValidUntil": "2025-12-31",
        "availability": "https://schema.org/InStock",
        "url": `https://www.frankotrading.com/product/${product.productID}`,
        "itemCondition": "https://schema.org/NewCondition",
        "seller": {
          "@type": "Organization",
          "name": "Franko Trading"
        },
        "shippingDetails": {
          "@type": "OfferShippingDetails",
          "shippingRate": {
            "@type": "MonetaryAmount",
            "value": "30.00",
            "currency": "GHS"
          },
          "shippingDestination": {
            "@type": "DefinedRegion",
            "addressCountry": "GH"
          },
          "deliveryTime": {
            "@type": "ShippingDeliveryTime",
            "handlingTime": {
              "@type": "QuantitativeValue",
              "minValue": 0,
              "maxValue": 1,
              "unitCode": "DAY"
            },
            "transitTime": {
              "@type": "QuantitativeValue",
              "minValue": 1,
              "maxValue": 5,
              "unitCode": "DAY"
            }
          }
        },
        "hasMerchantReturnPolicy": {
          "@type": "MerchantReturnPolicy",
          "name": "Standard Return Policy",
          "applicableCountry": "GH",
          "returnPolicyCategory": "https://schema.org/MerchantReturnFiniteReturnWindow",
          "merchantReturnDays": 7,
          "returnMethod": "https://schema.org/ReturnByMail",
          "returnFees": "https://schema.org/FreeReturnShipping",
          "refundType": "https://schema.org/FullRefund"
        }
      },
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.7",
        "reviewCount": "234"
      }
    }))
  }
};


  return (
    <div className="container mx-auto p-4 md:p-6">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={description} />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={description} />
        <meta property="og:image" content={imageUrl} />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={imageUrl} />
        <link rel="canonical" href={pageUrl} />
        <script type="application/ld+json">
          {JSON.stringify(productListSchema, null, 2)}
        </script>
      </Helmet>

      <h3 className="text-md font-semibold mb-4 text-red-500">
        {selectedBrand ? `${brandName} Products` : 'Brand Products'}
      </h3>

      <RelatedBrands
        filteredBrands={filteredBrands}
        selectedBrandId={brandId}
        navigate={navigate}
      />

      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {Array.from({ length: 12 }).map((_, index) => (
            <div
              key={index}
              className="animate-pulse border rounded-lg shadow p-3 bg-gray-100 relative"
            >
              <div
                className="absolute inset-0 bg-center bg-no-repeat opacity-10"
                style={{
                  backgroundImage: "url('/frankoIcon.png')",
                  backgroundSize: '90px',
                }}
              ></div>
              <div className="h-36 bg-gray-200 rounded-lg"></div>
              <div className="h-3 bg-gray-200 rounded w-3/4 mt-2"></div>
              <div className="h-3 bg-gray-200 rounded w-1/2 mt-1"></div>
            </div>
          ))}
        </div>
      ) : currentProducts.length > 0 ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {currentProducts.map((product, index) => {
            const pricing = calculatePricing(product);
            
            return (
              <div
                key={product.productID}
                ref={
                  index === currentProducts.length - 1 ? lastProductRef : null
                }
                className="p-4 bg-white border border-gray-200 rounded-lg shadow-md hover:shadow-xl transition-transform transform hover:scale-105 cursor-pointer relative"
                onClick={() => navigate(`/product/${product.productID}`)}
              >
                {/* Discount Badge */}
                <div className="absolute top-2 right-2 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full z-10">
                  -{pricing.discountPercentage}%
                </div>

                <div className="h-32 md:h-32 lg:h-48 flex items-center justify-center mb-3">
                  {!product.productImage ||
                  imageLoadStates[product.productID] === 'error' ? (
                    <div className="animate-pulse w-full h-full bg-gray-200 rounded-lg"></div>
                  ) : (
                    <>
                      {!imageLoadStates[product.productID] && (
                        <div className="animate-pulse w-full h-full bg-gray-200 rounded-lg"></div>
                      )}
                      <img
                        src={`https://smfteapi.salesmate.app/Media/Products_Images/${product.productImage.split('\\').pop()}`}
                        alt={product.productName}
                        className={`w-full h-full object-contain rounded-lg ${
                          !imageLoadStates[product.productID] ? 'hidden' : ''
                        }`}
                        onLoad={() => handleImageLoad(product.productID)}
                        onError={() => handleImageError(product.productID)}
                      />
                    </>
                  )}
                </div>
                
                <h4 className="text-sm font-medium truncate mb-2">
                  {product.productName}
                </h4>
                
                {/* Pricing Section */}
                <div className="flex flex-col space-y-1">
                  {/* Current Price */}
                  <div className="flex items-center space-x-1">
                    <span className="text-red-500 font-semibold text-sm">
                      ₵{pricing.currentPrice.toLocaleString()}.00
                    </span>
                  </div>
                  
                  {/* Old Price */}
                  <div className="flex items-center space-x-2">
                    <span className="text-gray-400 text-xs line-through">
                      ₵{pricing.oldPrice.toLocaleString()}.00
                    </span>
                    
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <Empty description="No products available" />
      )}
    </div>
  );
};

export default Brand;